import type { RaygunPayload } from "raygun4js";
import { useEffect, useRef } from "react";
import { EMAIL_KEY, FIRM_NAME_KEY } from "@/lib/constants";
import type { RaygunBreadcrumb } from "@/lib/types";
import { normalizeError } from "@/lib/utils";

const RAYGUN_APIKEY: string = import.meta.env.VITE_RAYGUN_API_KEY;
const APP_VERSION: string = import.meta.env.VITE_VERSION;
const VITE_API_URL: string = import.meta.env.VITE_API_URL;

let isRaygunInitialized = false;

export function useRaygun(): {
  sendError: (
    error: unknown,
    customData?: Record<string, unknown>,
  ) => Promise<void>;
  trackPageView: (path: string) => Promise<void>;
} {
  const storedFirmName = localStorage.getItem(FIRM_NAME_KEY);
  const storedEmail = localStorage.getItem(EMAIL_KEY);
  const isInitialized = useRef(false);
  const isDevMode = import.meta.env.MODE === "development";

  const sendError = async (
    error: unknown,
    customData?: Record<string, unknown>,
  ) => {
    // Disable error tracking in development mode.
    if (isDevMode) {
      return;
    }

    try {
      const rg4js = (await import("raygun4js")).default;
      rg4js("send", { error: normalizeError(error), customData });
    } catch (err) {
      console.error("Failed to send error to Raygun:", err);
    }
  };

  const trackPageView = async (path: string) => {
    // Disable page view tracking in development mode.
    if (isDevMode) {
      return;
    }

    try {
      const rg4js = (await import("raygun4js")).default;
      rg4js("trackEvent", { type: "pageView", path });
    } catch (err) {
      console.error("Failed to track page view in Raygun:", err);
    }
  };

  useEffect(() => {
    if (isDevMode || isInitialized.current || isRaygunInitialized) return;

    async function initializeRaygun() {
      if (!RAYGUN_APIKEY) {
        console.warn("Raygun API key is not set");
        return;
      }

      const rg4js = (await import("raygun4js")).default;

      rg4js("enableCrashReporting", true);
      rg4js("enableRum", true);
      if (import.meta.env.MODE === "production") {
        rg4js("enablePulse", true);
      }
      rg4js("apiKey", RAYGUN_APIKEY);
      rg4js("options", {
        disableAnonymousUserTracking: true,
        apiEndpoint: VITE_API_URL,
      });
      rg4js("setVersion", APP_VERSION?.replace("v", ""));
      rg4js("boot");

      const beforeSend = (
        payload: RaygunPayload & {
          Details: { Breadcrumbs?: RaygunBreadcrumb[] };
        },
      ):
        | (RaygunPayload & { Details: { Breadcrumbs?: RaygunBreadcrumb[] } })
        | boolean => {
        const breadcrumbs = payload?.Details?.Breadcrumbs;

        if (!breadcrumbs?.length) {
          return payload;
        }

        const newBreadcrumbs = [];
        for (const crumb of breadcrumbs) {
          if (!crumb) continue;

          const customData = crumb.CustomData;
          if (customData?.selector?.includes("TEXT")) {
            customData.text = "[REDACTED]";
          }
          newBreadcrumbs.push(crumb);
        }
        payload.Details.Breadcrumbs = newBreadcrumbs;
        return payload;
      };

      rg4js("onBeforeSend", beforeSend);
      isInitialized.current = true;
      isRaygunInitialized = true;
    }

    initializeRaygun().catch(console.error);
  }, []);

  useEffect(() => {
    if (!isRaygunInitialized) return;

    async function updateUser() {
      const rg4js = (await import("raygun4js")).default;

      if (storedEmail || storedFirmName) {
        rg4js("setUser", {
          identifier: storedEmail || "",
          email: storedEmail || "",
          fullName: storedFirmName || "",
        });
      }
    }

    updateUser().catch(console.error);
  }, [storedEmail, storedFirmName]);

  return { sendError, trackPageView };
}
